/**
 * LOUD/QUIET Metric — a coloured KPI tile: mono label, big tight number, and a small bordered
 * delta chip. Used across dashboards.
 *
 * Ported 1:1 from the compiled `_ds_bundle.js`. Layout is a 2-column grid ("1fr auto"): the
 * label spans both columns on row 1, the value takes column 1 of row 2, the delta chip takes
 * column 2 and bottom-aligns to the value's baseline box (`alignSelf: end`).
 *
 * Non-obvious decision: direction is encoded by an arrow glyph (↑ / ↓) as well as colour, so a
 * "down" delta is still readable without colour perception.
 */
import * as React from 'react';

export type MetricColor = 'yellow' | 'pink' | 'mint' | 'blue' | 'paper' | 'white';
export type MetricDeltaTone = 'up' | 'down';

export interface MetricProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Small uppercase mono caption. */
  label?: React.ReactNode;
  /** The headline number. */
  value?: React.ReactNode;
  /** Change chip content, e.g. "12%". Omit to hide the chip entirely. */
  delta?: React.ReactNode;
  /** Arrow + colour of the delta chip. @default "up" */
  deltaTone?: MetricDeltaTone;
  /** Tile fill. @default "yellow" */
  color?: MetricColor;
  style?: React.CSSProperties;
}

const BACKGROUNDS: Record<MetricColor, string> = {
  yellow: 'var(--lq-yellow)',
  pink: 'var(--lq-pink)',
  mint: 'var(--lq-mint)',
  blue: 'var(--lq-blue)',
  paper: 'var(--lq-paper)',
  white: 'var(--lq-white)',
};

export function Metric({
  label,
  value,
  delta,
  deltaTone = 'up',
  color = 'yellow',
  style,
  ...rest
}: MetricProps) {
  // Only the blue tile is dark enough to need inverted text.
  const fg = color === 'blue' ? 'var(--lq-paper)' : 'var(--lq-ink)';

  // The bundle tests `delta` for truthiness, which also swallows a supplied `delta={0}` — a
  // real reading ("no change"), and the one the API documents as visible. Hide the chip only
  // for the values that mean "nothing to render".
  const hasDelta = delta !== undefined && delta !== null && delta !== false && delta !== '';

  return (
    <div
      {...rest}
      style={{
        border: 'var(--lq-border)',
        background: BACKGROUNDS[color],
        color: fg,
        padding: 20,
        boxShadow: 'var(--lq-shadow-s)',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 7,
        fontFamily: 'var(--lq-font-sans)',
        ...style,
      }}
    >
      <span
        style={{
          gridColumn: '1/-1',
          fontFamily: 'var(--lq-font-mono)',
          fontSize: 9,
          fontWeight: 800,
          textTransform: 'uppercase',
          // NOTE: 0.06em, not the --lq-label-tracking token (0.08em) the readme prescribes for
          // mono labels. Bundle wins.
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </span>
      <strong
        style={{
          fontSize: 32,
          letterSpacing: '-0.04em',
          lineHeight: 1,
        }}
      >
        {value}
      </strong>
      {hasDelta ? (
        <small
          style={{
            alignSelf: 'end',
            // NOTE: on the blue tile the chip is a 15% white wash. The readme says
            // "transparency & blur: essentially none" and there is no alpha token for it, so
            // this stays a literal rgba(). It is the only translucent value in the system.
            background: color === 'blue' ? 'rgba(255,255,255,.15)' : 'var(--lq-white)',
            // NOTE: written literally in the bundle; equivalent to the --lq-border-hair token.
            border: '1px solid var(--lq-ink)',
            padding: '4px 6px',
            fontWeight: 800,
            fontSize: 12,
            color: deltaTone === 'down' ? 'var(--lq-red)' : fg,
          }}
        >
          {deltaTone === 'down' ? '↓' : '↑'} {delta}
        </small>
      ) : null}
    </div>
  );
}

export default Metric;
